<template>
  <div class="row">
    <div id="map" ref="map" class="map row__item" />
    <aside :style="infoWindowWidth" class="info-window">
      <div v-if="selectedMarker" class="info-window__close" @click="closeAside">
        <Icon id="close" />
      </div>
      <template v-if="selectedMarker">
        <RouterLink
          :to="{ name: 'project', params: { projectSlug: selectedProject.slug } }"
          class="card -projects"
          :aria-labelledby="getAriaLabel(selectedProject.title)"
        >
          <div class="card__content">
            <Image class="card__background" :source="selectedProject.image" />
            <div class="card__inner">
              <div class="row -spread -align-center -mobile">
                <p class="card__status">
                  <Icon :id="selectedProject.status" />
                  {{ getStatus(selectedProject.status) }}
                </p>
                <p class="card__category">
                  <Icon :id="`project-${selectedProject.category}`" class="category" />
                  {{ getCategory(selectedProject.category) }}
                </p>
              </div>
              <h3 class="card__title">{{ selectedProject.title }}</h3>
              <p class="card__location">
                <Icon id="project-location" />
                {{ selectedProject.region }}
              </p>
              <div class="card__inner-content" v-if="$mq.smPlus">
                <p class="card__label">{{ labels.details }}</p>
                <div>
                  <p class="card__details">{{ selectedProject.infrastructures }}</p>
                  <p class="card__details">{{ selectedProject.area }}</p>
                  <p class="card__details">{{ selectedProject.project_budget }}</p>
                </div>
              </div>

              <div class="link -after -selected-project">
                <span>{{ labels.info }}</span>
                <Icon id="leaves" />
              </div>
            </div>
          </div>
        </RouterLink>
      </template>
    </aside>
  </div>
</template>

<script>
import markerEpongeActive from '@/assets/images/marker-eponge-active.svg'
import markerEponge from '@/assets/images/marker-eponge.svg'
import markerOther from '@/assets/images/marker-other.svg'
import Icon from '@/components/global/Icon.vue'
import Image from '@/components/global/Image.vue'
import { categoryNames, statusNames } from '@/constants'

export default {
  name: 'GoogleMapViewport',
  props: {
    labels: { type: Object, required: true },
    projects: { type: Array, default: Array },
  },
  components: { Image, Icon },
  data() {
    return {
      googleMapsApiKey: import.meta.env.VITE_APP_GOOGLE_MAP_KEY,
      isGoogleApiLoaded: !!(window.google && window.google.maps),
      mapInstance: null,
      infoWindow: null,
      markerIcons: {
        eponge: markerEponge,
        epongeActive: markerEpongeActive,
        other: markerOther,
        otherActive: markerOther,
      },
      selectedMarker: null,
    }
  },
  created() {
    this.googleMarkers = []
  },
  computed: {
    infoWindowWidth() {
      if (this.selectedMarker && this.$mq.smPlus) {
        return { width: '24.5rem' }
      } else if (this.selectedMarker) {
        return { width: '90%' }
      }
      return { width: 0 }
    },
    selectedProject() {
      if (this.selectedMarker) {
        const project = { ...this.$beet.projects[this.selectedMarker] }
        project.image = project.images.length ? project.images[0] : null
        return project
      }
      return null
    },
  },
  watch: {
    projects: 'generateMarkers',
  },
  mounted() {
    this.$nextTick(() => {
      this.loadMap()
    })
  },
  methods: {
    getAriaLabel(projectTitle) {
      return `Plus d'infos sur ${projectTitle})`
    },
    getStatus(slug) {
      return statusNames[slug]
    },
    getCategory(slug) {
      return categoryNames[slug]
    },
    loadMap() {
      if (this.isGoogleApiLoaded) return this.generateMap()

      const googleMapsScript = document.createElement('script')
      googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&id=d3439c3cc19ada4e&libraries=places`

      googleMapsScript.addEventListener('load', () => {
        if (!window.google || !window.google.maps) return
        this.isGoogleApiLoaded = true
        this.generateMap()
      })

      document.head.appendChild(googleMapsScript)
    },
    generateMap() {
      const { maps } = window.google || {}

      this.mapInstance = new maps.Map(this.$refs.map, {
        center: { lat: 46.214, lng: -72.243 },
        disableDefaultUI: true,
        zoomControl: 1,
        zoom: 8,
        minZoom: 2,
        maxZoom: 17,
        mapId: '8c71ec6b49782f5f',
      })

      this.generateMarkers()
    },
    generateMarkers() {
      const { maps } = window.google || {}
      this.googleMarkers.forEach((marker) => {
        marker.setMap(null)
      })
      this.googleMarkers = []

      this.googleMarkers = this.projects.map((project) => {
        const marker = new maps.Marker({
          icon: this.getMarkerIcon(project.is_eponge),
          map: this.mapInstance,
          position: new maps.LatLng(project.latitude, project.longitude),
          title: project.title,
          id: project.id,
          isEponge: !!project.is_eponge,
          optimized: false,
        })

        marker.addListener('click', () => {
          this.selectedMarker = this.selectedMarker === project.id ? null : project.id
          this.updateMarkerIcons()
          if (this.$mq.smPlus) {
            this.mapInstance.panTo(marker.getPosition())
          } else {
            const divHeightOfTheMap = document.getElementById('map').clientHeight
            const offSetFromBottom = 50
            this.mapInstance.setCenter(marker.getPosition())
            this.mapInstance.panBy(0, -(divHeightOfTheMap / 2 - offSetFromBottom))
          }
        })
        return marker
      })
    },
    updateMarkerIcons() {
      this.googleMarkers.forEach((marker) => {
        marker.setIcon(this.getMarkerIcon(marker.isEponge, marker.id === this.selectedMarker))
      })
    },
    getMarkerIcon(isEponge, isActive) {
      const projectCategory = isEponge ? 'eponge' : 'other'
      const iconKey = isActive ? `${projectCategory}Active` : projectCategory
      return this.markerIcons[iconKey]
    },
    closeAside() {
      this.selectedMarker = null
    },
  },
}
</script>
